const readVeloraWishlist = (key) => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(stored) ? stored.filter((item) => item?.handle && item?.title && item?.url) : [];
  } catch (error) {
    return null;
  }
};

const writeVeloraWishlist = (key, items) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(items.slice(0, 50)));
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { key, items } }));
    return true;
  } catch (error) {
    return false;
  }
};

const populateVeloraWishlistCard = (item, product, removeLabel) => {
  item.querySelectorAll('[data-wishlist-link]').forEach((link) => { link.href = product.url; });
  const mediaLink = item.querySelector('.product-card__media');
  if (mediaLink) mediaLink.setAttribute('aria-label', product.title);
  const title = item.querySelector('[data-wishlist-title]');
  if (title) title.textContent = product.title;
  const price = item.querySelector('[data-wishlist-price]');
  if (price) price.textContent = product.price;
  const vendor = item.querySelector('[data-wishlist-vendor]');
  if (vendor && product.vendor) { vendor.textContent = product.vendor; vendor.hidden = false; }
  const soldOut = item.querySelector('[data-wishlist-sold-out]');
  if (soldOut) soldOut.hidden = product.available !== false;
  const remove = item.querySelector('[data-wishlist-remove]');
  if (remove) {
    remove.dataset.handle = product.handle;
    remove.setAttribute('aria-label', removeLabel.replace('__TITLE__', product.title));
  }
  const image = item.querySelector('[data-wishlist-image]');
  if (image && product.image) {
    image.src = product.image;
    image.alt = product.imageAlt || product.title;
    image.width = Number(product.imageWidth || 900);
    image.height = Number(product.imageHeight || 1125);
    image.hidden = false;
    item.querySelector('[data-wishlist-placeholder]')?.setAttribute('hidden', '');
  }
};

class WishlistToggle extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.button = this.querySelector('[data-wishlist-toggle]');
    this.label = this.querySelector('[data-wishlist-label]');
    this.savedLink = this.querySelector('[data-wishlist-page-link]');
    const productNode = this.querySelector('[data-wishlist-product]');
    if (!this.button || !productNode) return;
    try {
      this.product = JSON.parse(productNode.textContent);
    } catch (error) {
      this.hidden = true;
      return;
    }
    if (!this.product?.handle || readVeloraWishlist(this.dataset.storageKey) === null) {
      this.hidden = true;
      return;
    }
    this.onToggle = this.onToggle.bind(this);
    this.onUpdated = this.onUpdated.bind(this);
    this.button.addEventListener('click', this.onToggle);
    window.addEventListener('wishlist:updated', this.onUpdated);
    this.initialized = true;
    this.sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onToggle);
    window.removeEventListener('wishlist:updated', this.onUpdated);
    this.initialized = false;
  }

  onToggle() {
    const items = readVeloraWishlist(this.dataset.storageKey);
    if (!items) return;
    const saved = items.some((item) => item.handle === this.product.handle);
    const next = saved
      ? items.filter((item) => item.handle !== this.product.handle)
      : [{ ...this.product, savedAt: Date.now() }, ...items.filter((item) => item.handle !== this.product.handle)];
    if (writeVeloraWishlist(this.dataset.storageKey, next)) this.sync(next);
  }

  onUpdated(event) {
    if (event.detail?.key === this.dataset.storageKey) this.sync(event.detail.items);
  }

  sync(items = readVeloraWishlist(this.dataset.storageKey) || []) {
    const saved = items.some((item) => item.handle === this.product.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    this.button.classList.toggle('is-saved', saved);
    if (this.label) this.label.textContent = saved ? this.button.dataset.removeLabel : this.button.dataset.addLabel;
    if (this.savedLink) this.savedLink.hidden = !saved;
  }
}

if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);

class WishlistProducts extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.list = this.querySelector('[data-wishlist-list]');
    this.template = this.querySelector('[data-wishlist-template]');
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    this.unavailableState = this.querySelector('[data-wishlist-unavailable]');
    if (!this.list || !this.template) return;
    this.onRemove = this.onRemove.bind(this);
    this.onUpdated = this.onUpdated.bind(this);
    this.addEventListener('click', this.onRemove);
    window.addEventListener('wishlist:updated', this.onUpdated);
    this.initialized = true;
    this.renderProducts();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onRemove);
    window.removeEventListener('wishlist:updated', this.onUpdated);
    this.initialized = false;
  }

  onRemove(event) {
    const button = event.target.closest('[data-wishlist-remove]');
    if (!button) return;
    const items = readVeloraWishlist(this.dataset.storageKey);
    if (!items) return;
    writeVeloraWishlist(this.dataset.storageKey, items.filter((item) => item.handle !== button.dataset.handle));
  }

  onUpdated(event) {
    if (event.detail?.key === this.dataset.storageKey) this.renderProducts(event.detail.items);
  }

  renderProducts(items = readVeloraWishlist(this.dataset.storageKey)) {
    if (items === null) {
      this.list.hidden = true;
      if (this.emptyState) this.emptyState.hidden = true;
      if (this.unavailableState) this.unavailableState.hidden = false;
      return;
    }
    const visible = items.slice(0, Number(this.dataset.limit || 24));
    const fragment = document.createDocumentFragment();
    visible.forEach((product) => {
      const item = this.template.content.cloneNode(true);
      populateVeloraWishlistCard(item, product, this.dataset.removeLabel);
      fragment.appendChild(item);
    });
    this.list.replaceChildren(fragment);
    this.list.hidden = visible.length === 0;
    if (this.emptyState) this.emptyState.hidden = visible.length > 0;
    if (this.unavailableState) this.unavailableState.hidden = true;
  }
}

if (!customElements.get('wishlist-products')) customElements.define('wishlist-products', WishlistProducts);
